import { useEffect, useState } from 'react';
import { BrowserRouter, useLocation } from 'react-router-dom';
import { AppRoutes } from './router';
import FloatingButtons from './components/feature/FloatingButtons';
import { trackPageView } from './lib/analytics';
import { supabase, getWebsiteId } from './lib/supabase';
import MaintenancePage from './pages/maintenance/page';

function PageTracker() {
  const location = useLocation();

  useEffect(() => {
    trackPageView();
  }, [location.pathname]);

  return null;
}

function FaviconLoader() {
  useEffect(() => {
    const loadFavicon = async () => {
      try {
        const websiteId = await getWebsiteId();
        const { data } = await supabase
          .from('theme_settings')
          .select('logo_url, site_title')
          .eq('website_id', websiteId)
          .maybeSingle();

        if (data?.logo_url) {
          const existingFavicons = document.querySelectorAll('link[rel*="icon"]');
          existingFavicons.forEach(favicon => favicon.remove());

          const faviconSizes = [
            { rel: 'icon', type: 'image/png', sizes: '32x32' },
            { rel: 'icon', type: 'image/png', sizes: '16x16' },
            { rel: 'apple-touch-icon', sizes: '180x180' }
          ];

          faviconSizes.forEach(({ rel, type, sizes }) => {
            const link = document.createElement('link');
            link.rel = rel;
            if (type) link.type = type;
            if (sizes) link.sizes = sizes;
            link.href = data.logo_url;
            document.head.appendChild(link);
          });
        }

        if (data?.site_title) {
          document.title = data.site_title;
        }
      } catch (error) {
        console.error('Favicon yükleme hatası:', error);
      }
    };

    loadFavicon();
  }, []);

  return null;
}

function MaintenanceWrapper({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const [isMaintenanceMode, setIsMaintenanceMode] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkMaintenanceMode();
  }, []);

  const checkMaintenanceMode = async () => {
    try {
      const websiteId = await getWebsiteId();
      const { data } = await supabase
        .from('theme_settings')
        .select('site_maintenance')
        .eq('website_id', websiteId)
        .maybeSingle();
      
      if (data) {
        setIsMaintenanceMode(data.site_maintenance || false);
      }
    } catch (error) {
      console.error('Bakım modu kontrol hatası:', error);
    } finally {
      setLoading(false);
    }
  };

  // Admin sayfalarında bakım modunu gösterme
  const isAdminPage = location.pathname.startsWith('/admin');

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500"></div>
      </div>
    );
  }

  // Bakım modunda ve admin sayfası değilse bakım sayfasını göster
  if (isMaintenanceMode && !isAdminPage) {
    return <MaintenancePage />;
  }

  return <>{children}</>;
}

function App() {
  return (
    <BrowserRouter basename={__BASE_PATH__}>
      <PageTracker />
      <FaviconLoader />
      <MaintenanceWrapper>
        <AppRoutes />
        <FloatingButtons />
      </MaintenanceWrapper>
    </BrowserRouter>
  );
}

export default App;
